// Brand UI primitives — ported from DAI design system (client-owned tokens)
export const Container = ({ children, narrow = false, className = "", style = {} }) => (
  <div
    className={"container " + className}
    style={{
      maxWidth: narrow ? 880 : 1240,
      width: "100%",
      margin: "0 auto",
      paddingLeft: "var(--gutter)",
      paddingRight: "var(--gutter)",
      ...style,
    }}
  >
    {children}
  </div>
);

export const Rule = ({ width = 48, color = "var(--silver-mid)", style = {} }) => (
  <span style={{ display: "inline-block", width, height: 1, background: color, verticalAlign: "middle", ...style }} />
);

export const Eyebrow = ({ children, gold = false, inverse = false, withRule = true, testId }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 14 }} data-testid={testId}>
    {withRule && <Rule width={32} color={gold ? "var(--gold)" : inverse ? "rgba(255,255,255,0.4)" : "var(--silver-mid)"} />}
    <span style={{
      fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 500,
      color: gold ? "var(--gold-deep)" : inverse ? "rgba(255,255,255,0.75)" : "var(--silver-dark)",
    }}>{children}</span>
  </span>
);

export const Button = ({ children, variant = "primary", onClick, type = "button", icon = false, inverse = false, testId, disabled = false, style = {} }) => {
  const base = {
    fontFamily: "var(--font-body)", fontSize: 13, letterSpacing: "0.14em", fontWeight: 500,
    padding: "16px 26px", borderRadius: 2, cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 12,
    transition: "all 140ms cubic-bezier(.2,.6,.2,1)", border: "1px solid transparent",
    opacity: disabled ? 0.6 : 1,
  };
  const styles = {
    primary: { ...base, background: "var(--navy)", color: "var(--off-white)" },
    secondary: { ...base, background: "transparent", color: "var(--navy)", borderColor: "var(--silver-mid)" },
    ghost: { ...base, background: "transparent", color: inverse ? "var(--off-white)" : "var(--navy)", padding: "12px 0", borderRadius: 0,
      borderBottom: `1px solid ${inverse ? "rgba(255,255,255,0.4)" : "var(--navy)"}`, letterSpacing: "0.22em", textTransform: "uppercase", fontSize: 11 },
    inverse: { ...base, background: "transparent", color: "var(--off-white)", borderColor: "rgba(255,255,255,0.3)" },
    inversePrimary: { ...base, background: "var(--off-white)", color: "var(--navy)" },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} data-testid={testId} style={{ ...styles[variant], ...style }}>
      {children}
      {icon && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M7 17L17 7M9 7h8v8" />
        </svg>
      )}
    </button>
  );
};

export const Icon = ({ name, size = 18, style = {} }) => {
  const paths = {
    arrow: <path d="M7 17L17 7M9 7h8v8" />,
    chevron: <path d="M9 18l6-6-6-6" />,
    chevronDown: <path d="M6 9l6 6 6-6" />,
    check: <path d="M5 13l4 4L19 7" />,
    plus: <path d="M12 5v14M5 12h14" />,
    minus: <path d="M5 12h14" />,
    mail: <><rect x="3" y="5" width="18" height="14" rx="0.5" /><path d="M3 6l9 8 9-8" /></>,
    phone: <path d="M22 16.92v3a2 2 0 01-2.18 2 19.86 19.86 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.86 19.86 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8 10a16 16 0 006 6l1.36-1.36a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />,
    pin: <><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></>,
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    sparkle: <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" />,
    message: <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />,
    x: <path d="M18 6L6 18M6 6l12 12" />,
    send: <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />,
    alert: <><path d="M12 9v4M12 17h.01" /><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="square" style={style}>
      {paths[name]}
    </svg>
  );
};
